import { toDate } from '@art-suite/art-core-ts';
import { JsonArray, JsonObject, JsonValue, Simplify } from 'type-fest';
import { z, ZodArray, ZodObject, ZodOptional, ZodString, ZodType } from 'zod';
import 'zod-openapi';

export { z };
export type ZodSchema = z.ZodType;
export type { ZodType };

export type PlainObject<T = any> = Record<string, T>;

const isObject = (value: any): value is Record<string, any> => typeof value === 'object' && value !== null;
const isRegExp = (value: any): value is RegExp => value instanceof RegExp;
const isZodType = (value: any): value is ZodTypeAny => value instanceof z.ZodType;
const isPlainObject = (value: any): value is PlainObject =>
  // object inherits directly from Object
  typeof value === 'object' && value !== null && value.constructor === Object;

const isString = (value: any): value is string => typeof value === 'string';
const isNumber = (value: any): value is number => typeof value === 'number';
const isBoolean = (value: any): value is boolean => typeof value === 'boolean';
const isArray = (value: any): value is any[] => Array.isArray(value);

export type ZodTypeAny = z.ZodTypeAny;
export type ZodEmptyObject = z.ZodObject<{}>;
// ************************************************************
// Zod Type Exports
// ************************************************************
export const string = z.string();
export const presentString = string.min(1);
export const number = z.number();
export const boolean = z.boolean();
export const date = z.date();
export const bigint = z.bigint();
export const nonNegativeNumber = number.nonnegative();
export const positiveNumber = number.positive();
export const negativeNumber = number.negative();
export const nonNegativeInteger = nonNegativeNumber.int();
export const positiveInteger = positiveNumber.int();
export const negativeInteger = negativeNumber.int();
export const any = z.any();
export const znull = z.null();

export const optionalString = string.optional();
export const optionalNumber = number.optional();
export const optionalBoolean = boolean.optional();
export const optionalDate = date.optional();
export const optionalBigint = bigint.optional();
export const optionalNonNegativeNumber = nonNegativeNumber.optional();
export const optionalPositiveNumber = positiveNumber.optional();
export const optionalNegativeNumber = negativeNumber.optional();
export const optionalNonNegativeInteger = nonNegativeInteger.optional();
export const optionalPositiveInteger = positiveInteger.optional();
export const optionalNegativeInteger = negativeInteger.optional();

export const email = z.email();
export const url = z.url();
export const emoji = z.emoji();
export const uuid = z.uuid();
export const optionalUuid = uuid.optional();
export const nanoid = z.nanoid();
export const cuid = z.cuid();
export const cuid2 = z.cuid2();
export const ulid = z.ulid();
export const ip = z.union([z.ipv4(), z.ipv6()]);

// @ts-ignore
export const buffer = typeof Buffer !== 'undefined'
  // @ts-ignore
  ? z.instanceof(Buffer).meta({ type: 'string', format: 'binary' } as any)
  : undefined as unknown as ZodTypeAny;

export const datetimeString = z.iso.datetime();
export const dateString = z.iso.date();
export const dateToIsoDateTimeString = z.date().transform(date => date.toISOString());
export const timeString = z.iso.time();
export const durationString = z.iso.duration();
export const base64String = z.base64();

export const numberFromString = z.string().transform(str => parseFloat(str));
export const optionalNumberFromString = numberFromString.optional();

// ************************************************************
// ZodLess Schema
// ************************************************************
// Utility type to split a string into a tuple of literals
// The `string extends S` guard prevents infinite recursion when S is the generic string type
type Split<S extends string, Delimiter extends string> = string extends S ? string[]
  : S extends `${infer First}${Delimiter}${infer Rest}` ? [First, ...Split<Rest, Delimiter>]
  : [S];

type StripUndefined<T> = {
  [K in keyof T as T[K] extends undefined ? never : K]: T[K];
};

// Intersection type for string enum schemas: ZodType with .options so consumers can access enum values
type ZodLessEnumType<T extends string> = z.ZodType<T> & { readonly options: ReadonlyArray<T> };

// Helper type: recursively maps a schema definition to its corresponding Zod schema
// Utility type to enforce compatibility with ZodTypeAny
export type ZodLessSchemaToZod<T> =
  // Use `[T] extends [{ _zod: any }]` instead of `T extends ZodTypeAny` to avoid TS2589.
  // `T extends ZodTypeAny` checks structural compatibility against ZodType's 40+ methods, many of which
  // use `this` types (e.g., `.or(): ZodUnion<[this, T]>`), creating circular checks when T is a nested ZodType.
  // `[T] extends [{ _zod: any }]` only verifies one property (_zod), breaking the cycle.
  // The `[...]` wrapper prevents conditional type distribution over unions.
  [T] extends [{ _zod: any }] ? T & ZodTypeAny // Direct Zod type (cheap check; `T & ZodTypeAny` satisfies $ZodType trivially: A & B always extends B)
    : T extends RegExp ? ZodString // RegExp schema
    : T extends string ? ZodLessEnumType<Split<T, '|'>[number]> // String resulting in a ZodEnum with .options
    : T extends [infer U] ? ZodArray<ZodLessSchemaToZod<U>> // Array schema
    : T extends [] ? ZodArray<ZodTypeAny> // Empty array schema
    : T extends (...args: any[]) => any ? ZodTypeAny // Function types (e.g., array methods like push/pop) → fallback
    : T extends object ? ZodObject<{ [K in keyof T]: ZodLessSchemaToZod<T[K]> }> // Object schema
    : z.ZodTypeAny; // Explicit fallback for unknown types

export type ZodLessSchemaArrayToZodArray<T> = T extends [infer U] ? ZodArray<ZodLessSchemaToZod<U>> : never;

export type ZodLessInfer<T> = Simplify<z.infer<ZodLessSchemaToZod<T>>>;
export type ZodLessInferInput<T> = Simplify<z.input<ZodLessSchemaToZod<T>>>;
export type ZodLessInferOutput<T> = Simplify<z.output<ZodLessSchemaToZod<T>>>;

export type ZodLessMetadata<T> = {
  description?: string;
  example?: T;
  examples?: [T, ...T[]];
  ref?: string;
  default?: T;
};

// Use `{ _zod: any }` instead of `ZodTypeAny` for the ZodType branch.
// Checking `T extends ZodTypeAny` requires structural verification of 40+ ZodType methods with
// `this` types, creating circular type checks and causing TS2589 in cross-package contexts.
// `{ _zod: any }` is satisfied by all Zod 4 schemas (they all have `_zod`) and is much cheaper to check.
export type ZodLessSchema =
  | [ZodLessSchema]
  | []
  | { [key: string]: ZodLessSchema | undefined }
  | RegExp
  | string
  | { _zod: any }; // All Zod 4 schemas have _zod (cheaper discriminant than ZodTypeAny)

// ************************************************************
// Helpers
// ************************************************************

const zodLessCore = <T extends ZodLessSchema>(schema: T): ZodLessSchemaToZod<T> => {
  switch (true) {
    case isZodType(schema):
      return schema as ZodLessSchemaToZod<T>;
    case isArray(schema):
      return schema.length === 0 ? z.array(z.any()) as unknown as ZodLessSchemaToZod<T> : z.array(zodLess(schema[0])) as unknown as ZodLessSchemaToZod<T>;
    case isRegExp(schema):
      return z.string().regex(schema) as ZodLessSchemaToZod<T>;
    case isObject(schema):
      return z.object(
        Object.fromEntries(Object.entries(schema).filter(([key, value]) => value).map(([key, value]) => [key, zodLess(value)])),
      ) as unknown as ZodLessSchemaToZod<T>;
    case isString(schema):
      return z.enum(schema.split('|') as [string, ...string[]]) as unknown as ZodLessSchemaToZod<T>;
    default:
      return schema as ZodLessSchemaToZod<T>;
  }
};

// ************************************************************
// ZodLess - Nicely Formatted Error Messages
// ************************************************************
let __seen = new WeakSet();

const getInspectName = (obj: any): string => {
  if (obj == null) return '' + obj;
  return obj.name || obj.constructor.name || obj.toString();
};

const stripNonJsonRecursive = (obj: any): any => {
  if (isPlainObject(obj) || isArray(obj)) {
    if (__seen.has(obj)) return `<circular reference to ${getInspectName(obj)}>`;
    __seen.add(obj);
  }
  if (isPlainObject(obj)) return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, stripNonJsonRecursive(value)]));
  if (isArray(obj)) return obj.map(stripNonJsonRecursive);
  if (isString(obj)) return obj;
  if (isNumber(obj)) return obj;
  if (isBoolean(obj)) return obj;
  return `<unserializable ${getInspectName(obj)}>`;
};

const stripNonJson = (obj: any): any => {
  __seen = new WeakSet();
  return stripNonJsonRecursive(obj);
};

const safeStringify = (obj: any, space: number = 2) => JSON.stringify(stripNonJson(obj), null, space);

export const zodLessErrorMessage = (zod: ZodTypeAny, data: any) => {
  const requestValidationResult = zod.safeParse(data);
  if (requestValidationResult.success) throw new Error('Expecting there to be errors');
  return requestValidationResult.error.issues
    .map(issue => {
      // Navigate the original input using the issue path to get the invalid data
      let dataAtPath: any = data;
      for (const key of issue.path) {
        if (dataAtPath && typeof dataAtPath === 'object' && key in dataAtPath) {
          dataAtPath = dataAtPath[key];
        } else {
          dataAtPath = undefined;
          break;
        }
      }

      // Convert data to string and limit its length
      const dataSnippet = dataAtPath != null ? JSON.stringify(dataAtPath) : 'undefined';
      const dataDisplay = dataSnippet.length > 100 ? dataSnippet.slice(0, 97) + '...' : dataSnippet;

      let pathString = issue.path.length > 0 ? `\`${issue.path.join('.')}\` ` : '';
      pathString = pathString
        .replace(/\.["]([^"]+)["]/g, '["$1"]')
        .replace(/\.[']([^']+)[']/g, '["$1"]')
        .replace(/\.(\d+)/g, '[$1]')
        .trim();
      if (!issue.message.includes(dataDisplay.replace(/^['"]|['"]$/g, ''))) {
        return `${pathString} was ${dataDisplay}. ${issue.message}`;
      } else return `${pathString} ${issue.message}`;
    })
    .join('\n');
};

export const zodLessErrorMessageDetailed = (zod: ZodTypeAny, data: any) =>
  zodLessErrorMessage(zod, data)
  + `\n\nSchema: ${safeStringify(zodLessToJsonSchema(zod))}`
  + `\n\nData received: ${safeStringify(data)}`;

/**
 * zodLessParse(schema, data) => data parsed by schema
 *
 * Parse, but with a nice error message if it fails.
 *
 * Throws an error if data is not valid for schema
 *
 * @param schema
 * @param data
 * @param context - optional string to include with the error
 */
export const zodLessParse = <T extends ZodLessSchema>(schema: T, data: any, context?: string): ZodLessInfer<T> => {
  const zSchema = zodLess(schema) as z.ZodType;
  const result = zSchema.safeParse(data);
  if (!result.success) {
    throw new Error((context ? context + ' ' : '') + '\n' + zodLessErrorMessage(zSchema, data));
  }
  return result.data as any;
};

export const curriedZodLessParse = <T extends ZodLessSchema>(schema: T) => (data: any, context?: string) => zodLessParse(schema, data, context);

/**
 * zodLessParseOrUndefined(schema, data) => data parsed by schema, or undefined if data is not valid for schema
 *
 * @param schema - ZodLess schema to parse
 * @param data - Data to parse
 */
export const zodLessParseOrUndefined = <T extends ZodLessSchema>(schema: T, data: any): ZodLessInfer<T> | undefined => {
  const result = (zodLess(schema) as z.ZodType).safeParse(data);
  if (!result.success) {
    return undefined;
  }
  return result.data as any;
};

export const zodLessParseJsonOrDefault = (schema: ZodLessSchema, json: string, defaultValue: any): any => {
  try {
    const parsed = (zodLess(schema as any) as z.ZodType).safeParse(JSON.parse(json));
    return parsed.success ? parsed.data : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const makeZodLessParser = <T extends ZodLessSchema>(schema: T) => (data: any) => zodLessParse(schema, data);

// ************************************************************
// ZodLess
// ************************************************************
// NOTE: zodLess uses z.object({}) to define objects, and z.object isn't strict by default - so extra fields are allowed and ignored.
// You'll need to call .strict() to make it strict, and note that it'll only do that on the top level object.
export const zodLess = <T extends ZodLessSchema>(schema: T, meta?: ZodLessMetadata<any>): ZodLessSchemaToZod<T> => {
  let returnSchema = zodLessCore(schema);
  if (meta) {
    // Use .meta() for OpenAPI metadata (zod-openapi v5 native approach)
    const metaObj: Record<string, any> = {};
    if (meta.description) metaObj.description = meta.description;
    if (meta.example) metaObj.example = meta.example;
    if (meta.examples) metaObj.examples = meta.examples;
    if (meta.ref) metaObj.id = meta.ref; // ref → id in zod-openapi v5

    if (Object.keys(metaObj).length > 0 && typeof (returnSchema as any).meta === 'function') {
      returnSchema = (returnSchema as any).meta(metaObj) as typeof returnSchema;
    } else if (meta.description || meta.example || meta.ref) {
      const desc: string[] = [];
      if (meta.description) desc.push(meta.description);
      if (meta.example) desc.push('Example: ' + JSON.stringify(meta.example));
      if (meta.ref) desc.push(`Reference: ${meta.ref}`);
      returnSchema = (returnSchema as any).describe(desc.join('\n\n')) as typeof returnSchema;
    }

    if (meta.default && (returnSchema as any).default) {
      returnSchema = (returnSchema as any).default(meta.default) as typeof returnSchema;
    }
  }
  return returnSchema;
};

const NO_META = {};
// required and optional work the same as zodLess, the latter also applies the optional modifier
export const required = zodLess;
export const optional = <T extends ZodLessSchema>(schema: T, meta?: ZodLessMetadata<any>): ZodOptional<ZodLessSchemaToZod<T>> =>
  (zodLess(schema) as z.ZodType).optional().meta(meta ?? NO_META) as any;

/**
 * ifExists(schema) returns a zod schema that is optional and nullable, and if it's null, convert to undefined
 *
 * @param schema - ZodLess schema to convert
 * @param meta - Metadata for the schema
 * @returns Zod schema that is optional and nullable, and if it's null, convert to undefined
 */
export const ifExists = <T extends ZodLessSchema>(schema: T, meta?: ZodLessMetadata<any>): ZodOptional<ZodLessSchemaToZod<T>> =>
  (zodLess(schema) as z.ZodType).nullable().optional().transform(value => value === null ? undefined : value).meta(meta ?? NO_META) as any;

// ************************************************************
// isTypeOfZod
// ************************************************************

/* isTypeOfZod(zodSchema, value) => true if value is of type zodSchema

Why isTypeOfZod?

  zod.parse() throws errors
  zod.safeParse() returns an object with a success property
  but neither let us test if the value is of a specific type,
  especially not in a way that informs typescript if the value actually is of the type.

example:

  const zod = z.enum((["a", "b", "c"]);
  const anyValue: string = "a"
  const myObj = {a: 1, b: 2, c: 3}
  if (isTypeOfZod(zod, anyValue)) {
    // typescript now knows that anyValue is of type "a" | "b" | "c"
    myObj[anyValue] // typescript knows that myObj[anyValue] is OKAY!
  }

also, isTypeOfZod is curriable:

  const abcZod = z.enum((["a", "b", "c"]);
  const isAbcZod = isTypeOfZod(abcZod)
  // isAbcZod is now a function that takes a value and returns true if it is of type "a" | "b" | "c"

  // very useful for this:
  const someStringArray: string[]
  const onlyAbcs: ("a" | "b" | "c")[] = someStringArray.filter(isTypeOfZod(abcZod))

*/
// Overloads to handle different argument cases
export function isTypeOfZod<T extends z.ZodType>(
  zod: T | undefined | null,
): (value: unknown) => value is z.infer<T>;
export function isTypeOfZod<T extends z.ZodType>(
  zod: T | undefined | null,
  value: unknown,
): value is z.infer<T>;

// Implementation
export function isTypeOfZod<T extends z.ZodType>(zod: T | undefined | null, value?: unknown) {
  if (!zod) return false;
  if (arguments.length === 1) {
    return (value: unknown): value is z.infer<T> => zod.safeParse(value).success;
  } else {
    return zod.safeParse(value).success;
  }
}

// ************************************************************
// Advanced ZodLess Builders
// ************************************************************
export const zenum = <T extends [string, ...string[]]>(...values: T) => z.enum(values);

// Use T[number] (element type) instead of { [K in keyof T]: ... } to avoid iterating over
// all array method keys (push, pop, flat...) which would trigger infinite ZodLessSchemaToZod recursion.
export const zunion = <T extends [ZodLessSchema, ZodLessSchema, ...ZodLessSchema[]]>(...args: T): z.ZodType<z.infer<ZodLessSchemaToZod<T[number]>>> =>
  z.union(args.map(schema => zodLess(schema)) as any) as any;

export const zrecord = <T extends ZodLessSchema, V extends ZodLessSchema>(valueSchema: V): z.ZodRecord<z.ZodString, ZodLessSchemaToZod<V>> =>
  z.record(z.string(), zodLess(valueSchema)) as any;

export const zintersection = <A extends ZodLessSchema, B extends ZodLessSchema>(a: A, b: B): z.ZodType<any> =>
  z.intersection(zodLess(a) as any, zodLess(b) as any) as any;

// apiDate: accepts Date or DateString as inputs, but it always outputs a Date
// JSON doesn't support Dates, so over the API, we must use a date string
export const apiDate = zunion(
  string.refine(val => !isNaN(Date.parse(val)), { message: 'Invalid date string' }),
  date,
  number,
)
  .transform(val => (val instanceof Date ? val : new Date(val)))
  .meta({ type: 'string', format: 'date-time' });

export const optionalApiDate = apiDate.optional();

// apiDateString accepts Dates, strings or numbers, but it always outputs a string
export const apiDateString = zunion(
  apiDate,
  string.refine(val => !isNaN(Date.parse(val)), { message: 'Invalid date string' }),
  number,
).transform(val => toDate(val).toISOString())
  .meta({ type: 'string', format: 'date-time' });

// queryBoolean accepts strings "", null, undefined, "false" === false otherwise true, but it always outputs a boolean
export const queryBoolean = zunion(
  boolean,
  string.transform(val => val === 'false' || val === '' || val == null ? false : true),
);
export const optionalQueryBoolean = queryBoolean.optional();

export const queryNumber = string
  .transform(val => {
    try {
      return parseFloat(val);
    } catch {
      return undefined;
    }
  })
  .refine(val => isNumber(val) && !isNaN(val), { message: 'Invalid number string' });
export const optionalQueryNumber = queryNumber.optional();

// ************************************************************
// JsonSchema
// ************************************************************
export const JsonPrimitiveSchema = zunion(string, number, boolean, znull);
// Use z.array/z.record directly to avoid ZodLessSchemaToZod expanding the recursive JsonValue type (TS2589)
export const JsonArraySchema: z.ZodType<JsonArray> = z.lazy(() => z.array(JsonSchema as z.ZodType<JsonValue>)) as z.ZodType<JsonArray>;
export const JsonObjectSchema: z.ZodType<JsonObject> = z.lazy(() => z.record(z.string(), JsonSchema as z.ZodType<JsonValue>)) as z.ZodType<JsonObject>;
export const JsonSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    JsonPrimitiveSchema,
    JsonArraySchema,
    JsonObjectSchema,
  ])
) as z.ZodType<JsonValue>;

/**
 * zodLessToJsonSchema(schema) => JsonSchema for the given ZodLess schema
 *
 * @param schema - ZodLess schema to convert to JsonSchema
 * @returns JsonSchema for the given ZodLess schema
 */
export const zodLessToJsonSchema = (schema: ZodLessSchema, schemaName?: string): JsonObject =>
  z.toJSONSchema(zodLess(schema) as any, { unrepresentable: 'any', cycles: 'ref' }) as JsonObject;
